import React, { useEffect, useState, useRef } from 'react';
import { Alert, Box, Button, CardHeader, Grid, IconButton, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpressionsControl from '../../shared/config/ExpressionsControl';
import { CONFIG_ID_SELECTOR_CONTROL_ID, IConfigurationIdEntry, IExpression, IOtherControl } from '../../../models/configuration/plugin/IConfiguration';
import { getUpdatedPersonas } from '../../../utils/personaHelper';
import { usePersonas } from '../../../hooks/usePersonas';
import { DtCard } from '../../ui/card';
import DtTabs from '../../ui/tab/DtTabs';
import DtList from '../../ui/list/DtList';
import { DtConfirmDialog, DtDialog } from '../../ui/dialog';
import DtForm from '../../ui/form/DtForm';
import { EmptyState } from '../../ui/emptyState';
import { AccountCircle, InfoOutlined } from '@mui/icons-material';
import DtAlert from '../../ui/alert/DtAlert';
import ConfigurationIdsTab from './ConfigurationIdsTab';

interface tabControlProps {
    tabIndex: number,
    selectedControl: IOtherControl | null;
    updateParentControl: (updatedControl: IOtherControl) => void;
    sendUserUpdate: (users: string[]) => void;
    children?: React.ReactNode;
    configurationIds?: IConfigurationIdEntry[];
    updateConfigurationIds?: (entries: IConfigurationIdEntry[]) => void;
}

const initialSelectedControlState = {
    ControlBehavior: "",
    FriendlyName: "",
    Id: "",
    Caption: "",
    Enabled: false,
    Expressions: [],
    Roles: [],
    Users: [],
    EnableForTpoUser: false
}

const TabControl: React.FC<tabControlProps> = ({ selectedControl, updateParentControl, sendUserUpdate, configurationIds, updateConfigurationIds }) => {

    const personas = usePersonas(); // Use custom hook for personas
    const [selectedControlState, setSelectedControlState] = useState<IOtherControl>(initialSelectedControlState);
    const [selectedRoles, setSelectedRoles] = useState<string[] | undefined>([]);
    const [users, setUsers] = useState<string[] | undefined>([]);
    const [addUserDialogOpen, setAddUserDialogOpen] = useState(false);
    const [removeUserDialogOpen, setRemoveUserDialogOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<string | null>(null);
    const [tabValue, setTabValue] = React.useState(0);

    // Ref for DtForm
    const addUserFormRef = useRef<HTMLFormElement>(null);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
    };

    // Selected Control Changed
    useEffect(() => {
        if (selectedControl) {
            setSelectedControlState(selectedControl);
            setTabValue(0);
        }
    }, [selectedControl]);

    // Updated Personas and Users when selectedControlState changes
    useEffect(() => {
        if (selectedControlState) {
            setSelectedRoles(selectedControlState?.Roles);
            setUsers(selectedControlState?.Users);
        } else {
            setSelectedRoles([]);
            setUsers([]);
        }
    }, [selectedControlState]);

    /* Personas (stored as Roles) */
    const onPersonaSelected = (item: string) => {
        const updatedRoles = getUpdatedPersonas(selectedRoles, item);

        setSelectedRoles(updatedRoles);

        if (selectedControlState) {
            const updatedControl = { ...selectedControlState, Roles: updatedRoles };
            updateParentControl(updatedControl);
        }
    }

    /* Users */

    // Add User
    const handleAddUserDialogOpen = () => setAddUserDialogOpen(true);
    const handleAddUserDialogClosed = () => setAddUserDialogOpen(false);
    const handleAddUserValue = (value: string) => {
        if (!value || value.trim() === "") {
            return "Username is required";
        }
        
        const trimmedValue = value.trim();
        if (users?.includes(trimmedValue)) {
            return "Username already exists";
        }
        else {
            let userList = users || [];
            const updatedUserList = [...userList, trimmedValue];
            setUsers(updatedUserList);
            sendUserUpdate(updatedUserList);
            return "";
        }
    }


    // Remove User
    const handleRemoveUserDialogOpen = () => { setRemoveUserDialogOpen(true); }
    const handleRemoveUserDialogClosed = () => { setRemoveUserDialogOpen(false); }
    const handleRemoveUserValue = (user: string | null) => {
        if (user && users) {
            const updatedUsers = users.filter(currentUser => {
                return currentUser !== user
            });
            setUsers([...updatedUsers]);
            sendUserUpdate([...updatedUsers]);
            setRemoveUserDialogOpen(false);
        }
    };

    /* Expressions */
    const updateExpressions = (updatedExpressions: IExpression[]) => {
        if (selectedControlState && selectedControlState.Expressions) {
            const newControl = { ...selectedControlState, Expressions: updatedExpressions };
            setSelectedControlState(newControl);
            updateParentControl(newControl);
        }
    }

    const isConfigIdControl = selectedControlState.Id === CONFIG_ID_SELECTOR_CONTROL_ID;

    const tabLabels = [
        { label: "Security" },
        { label: "Expressions" },
        ...(isConfigIdControl ? [{ label: "Configuration IDs" }] : []),
    ];

    return (
        <DtTabs
            tabs={tabLabels}
            value={tabValue}
            onChange={handleTabChange}
        >
            {/* Security Tab */}
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <DtCard header="Choose Personas with Access">
                        <DtAlert severity='info' icon={<InfoOutlined />}>Select one or more personas from the list</DtAlert>
                        <Box sx={{ flex: 1, overflowY: 'auto', maxHeight: { md: '30vh', lg: '39vh', xl: '48vh' } }}>
                            <DtList
                                items={personas.map(p => ({
                                    id: p,
                                    label: p,
                                    checked: selectedRoles?.includes(p),
                                }))}
                                selectable
                                onSelect={(id, checked) => onPersonaSelected(id as any)}
                            />
                        </Box>
                    </DtCard>
                    
                </Grid>
                <Grid item xs={6}>
                    <DtCard header="Add Users with Access" actions={<>
                        {selectedControlState && (
                            <Button variant="contained" size="small"
                                onClick={handleAddUserDialogOpen}>Add</Button>
                        )}
                    </>}>

                        {addUserDialogOpen &&
                            <DtDialog
                                maxWidth='xs'
                                actions={
                                    <>
                                        <Button size='small' onClick={handleAddUserDialogClosed}>Cancel</Button>
                                        <Button
                                            size='small'
                                            variant='contained'
                                            type="button"
                                            onClick={() => {
                                                if (addUserFormRef.current) {
                                                    addUserFormRef.current.submit();
                                                }
                                            }}
                                        >
                                            Submit
                                        </Button>
                                    </>
                                }
                                title="Add New User"
                                open={addUserDialogOpen}
                                onClose={handleAddUserDialogClosed}
                            >
                                <DtForm
                                    ref={addUserFormRef}
                                    fields={[
                                        {
                                            id: "username",
                                            label: "Enter a Username",
                                            type: "text",
                                            required: true,
                                            size: 12,
                                            validate: (value) => {
                                                if (value && users?.includes(value.trim())) {
                                                    return "Username already exists";
                                                }
                                                return undefined;
                                            },
                                            showValidationStatusIcon: true,
                                        }
                                    ]}
                                    onSubmit={(data) => {
                                        const errorMessage = handleAddUserValue(data.username);
                                        if (!errorMessage) {
                                            handleAddUserDialogClosed();
                                        }
                                    }}
                                    showReset={false}
                                    showSubmit={false}
                                    gap={2}
                                />
                            </DtDialog>
                        }

                        {removeUserDialogOpen && (
                            <DtConfirmDialog
                                maxWidth='xs'
                                open={removeUserDialogOpen}
                                onClose={handleRemoveUserDialogClosed}
                                title="Remove User"
                                message={
                                    <>
                                        Are you sure you want to remove user <b>{selectedUser}</b>?
                                    </>
                                }
                                confirmColor='primary'
                                confirmLabel='Remove'
                                onConfirm={() => handleRemoveUserValue(selectedUser)}
                            />
                        )}

                        <Box>
                            <DtAlert severity='info' icon={<InfoOutlined />}>You can add multiple users</DtAlert>
                            <Box sx={{ flexGrow: 1, overflowY: 'auto',minHeight: { md: '30vh', lg: '37vh', xl: '47vh' }, maxHeight: { md: '30vh', lg: '37vh', xl: '47vh' }, px: 1 }}>
                                {users && users.length > 0 ? (
                                    <DtList
                                        items={(users ?? []).map(u => ({
                                            id: u,
                                            label: <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>{u}</Typography>
                                            </Box>,
                                            actionConfig: {
                                                mode: "inline",
                                                actions: [
                                                    {
                                                        type: "delete",
                                                        onClick: () => {
                                                            setSelectedUser(u);
                                                            handleRemoveUserDialogOpen();
                                                        },
                                                        label: "Delete"
                                                    }
                                                ]
                                            }
                                        }))}
                                        selectable={false}
                                        listItemSx={{ borderBottom: 1, borderColor: 'divider', borderRadius: 1, mb: 1 }}
                                    />
                                ) : <EmptyState
                                  subheader='Please add new user using the button above'
                                  icon={<AccountCircle fontSize="large" color="primary" />}
                                  minHeight={200}
                                  header={'No Users Found'}></EmptyState>}
                            </Box>
                        </Box>
                    </DtCard>
                </Grid>
            </Grid>
            {/* Expressions Tab */}
            <ExpressionsControl
                expressions={selectedControlState?.Expressions}
                updateParentExpressions={updateExpressions}
                            title="Any 'False' Expression will suppress/hide control"
            />
            {/* Configuration IDs Tab — only for configurationIdSelector control */}
            {isConfigIdControl && (
                <ConfigurationIdsTab
                    configurationIds={configurationIds ?? []}
                    updateConfigurationIds={updateConfigurationIds ?? (() => {})}
                />
            )}
        </DtTabs>
    );
};

export default TabControl;